import { getBoundsOfDistance } from 'geolib'

import type { V3PlaceSuggestion } from '@app/packages/api/v3/types/lib'
import type { Bounds } from '@app/packages/geo/Bounds'
import type { AddressKind } from '@app/packages/geo/MapBounds'
import type { Point } from '@app/packages/geo/Point'

import type { GeocodeData } from './addressTools'

/** A dropdown row. `place` is set when it came from our backend: a pick of it needs no geocode. */
export type AddressSuggestion = { value: string; place?: V3PlaceSuggestion }

export type AddressSuggestFilter = { kind?: 'locality' }

/** The rows last shown, by the string the input shows, so a pick is answered with what came along with it. */
export class AddressSuggestionMemo {
  private byValue = new Map<string, AddressSuggestion>()

  remember(suggestions: AddressSuggestion[]) {
    this.byValue = new Map()
    for (const suggestion of suggestions) {
      if (!this.byValue.has(suggestion.value)) this.byValue.set(suggestion.value, suggestion)
    }
  }

  /** A string nobody suggested — a recent address, a default city, a label off the map — comes back bare. */
  recall(value: string): AddressSuggestion {
    return this.byValue.get(value) ?? { value }
  }
}

/** The full address: the title with where it is. A row's value, so one street name in two cities stays two rows, and its label. */
export const placeSuggestionValue = (place: V3PlaceSuggestion) => [place.attributes.title, place.attributes.subtitle].filter(Boolean).join(', ')

/** What a geocode of the pick is: built from the suggestion alone. */
export function placeSuggestionGeocodeData(place: V3PlaceSuggestion): GeocodeData {
  const { latitude, longitude, locality, kind, precision } = place.attributes
  const location = { lat: latitude, lon: longitude }
  const addressKind = placeSuggestionKind(kind)
  return {
    label: placeSuggestionValue(place),
    city: locality ?? '',
    kind: addressKind,
    location,
    bounds: placeSuggestionBounds(location, addressKind, precision),
  }
}

export function placeSuggestionKind(kind: string | null): AddressKind {
  if (kind === 'house' || kind === 'street' || kind === 'locality') return kind
  return 'other'
}

/**
 * The server sends a point and no extent, so the box is drawn around it at the coarser of what
 * the address is and how precise its point is: a house on its street's point gets the street's box.
 */
export function placeSuggestionBounds(location: Point, kind: AddressKind, precision: string | null): Bounds {
  const halfSizeMeters = Math.max(kindHalfSize(kind), precisionHalfSize(precision))
  const [southWest, northEast] = getBoundsOfDistance({ latitude: location.lat, longitude: location.lon }, halfSizeMeters)
  return {
    bottomLeft: { lat: southWest.latitude, lon: southWest.longitude },
    topRight: { lat: northEast.latitude, lon: northEast.longitude },
  }
}

/** Two decimals is about a kilometre: enough to rank by city, too coarse to place anyone. */
export function suggestNearHint(location: Point): { latitude: number; longitude: number } {
  return { latitude: roundTo2(location.lat), longitude: roundTo2(location.lon) }
}

const HOUSE_HALF_SIZE_METERS = 500
const STREET_HALF_SIZE_METERS = 1000
/** The radius the default search address is drawn with, in `defaultSearchAddressResultSelector`. */
const LOCALITY_HALF_SIZE_METERS = 10000

function kindHalfSize(kind: AddressKind): number {
  if (kind === 'house') return HOUSE_HALF_SIZE_METERS
  if (kind === 'street') return STREET_HALF_SIZE_METERS
  return LOCALITY_HALF_SIZE_METERS
}

/** `exact`, `nearest_house` and an unknown or missing precision leave the size to the kind. */
function precisionHalfSize(precision: string | null): number {
  if (precision === 'street') return STREET_HALF_SIZE_METERS
  if (precision === 'locality') return LOCALITY_HALF_SIZE_METERS
  return HOUSE_HALF_SIZE_METERS
}

const roundTo2 = (value: number) => Math.round(value * 100) / 100
